/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    this.initMilestoneProgress();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.initMilestoneProgress();
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.initMilestoneProgress();
    });

    theme.addDelegateEventListener(this, 'click', '.quantity-down, .quantity-up, .cart-item__remove', () => {
      window.requestAnimationFrame(() => {
        this.updateMilestoneProgressFromInputs();
      });
    });

    theme.addDelegateEventListener(this, 'input', '.cart-item__quantity-input', () => {
      this.updateMilestoneProgressFromInputs();
    });

    theme.addDelegateEventListener(this, 'change', '.cart-item__quantity-input', () => {
      this.updateMilestoneProgressFromInputs();
    });

    theme.addDelegateEventListener(this, 'click', '.js-cart-drawer-upsell-add', (evt) => {
      this.handleUpsellAdd(evt);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  initMilestoneProgress() {
    this.milestoneProgress = this.querySelector('[data-milestone-progress]');
    if (!this.milestoneProgress) return;

    const currentTotal = Number(this.milestoneProgress.dataset.currentTotalCents || 0);
    this.updateMilestoneProgress(currentTotal);
  }

  updateMilestoneProgressFromInputs() {
    if (!this.milestoneProgress) {
      this.initMilestoneProgress();
    }

    if (!this.milestoneProgress) return;

    const nextTotal = this.getOptimisticCartTotal();
    this.updateMilestoneProgress(nextTotal);
  }

  getOptimisticCartTotal() {
    let total = 0;

    this.querySelectorAll('.cart-item[data-cart-item-unit-price]').forEach((item) => {
      const unitPrice = Number(item.dataset.cartItemUnitPrice || 0);
      const quantityInput = item.querySelector('.cart-item__quantity-input');
      const quantity = Math.max(0, Number(quantityInput ? quantityInput.value : item.dataset.cartItemQuantity || 0));

      total += unitPrice * quantity;
    });

    return total;
  }

  updateMilestoneProgress(totalCents) {
    if (!this.milestoneProgress) return;

    this.milestoneProgress.dataset.currentTotalCents = totalCents;

    const maxTarget = Number(this.milestoneProgress.dataset.maxTargetCents || 0);
    const fill = this.milestoneProgress.querySelector('[data-milestone-fill]');
    const status = this.milestoneProgress.querySelector('[data-milestone-status]');
    const markerEls = Array.from(this.milestoneProgress.querySelectorAll('.cart-milestones__marker[data-milestone-target-cents]'));
    const cardEls = Array.from(this.milestoneProgress.querySelectorAll('.cart-milestone-card[data-milestone-target-cents]'));

    if (fill) {
      const percent = maxTarget > 0 ? Math.min(100, (totalCents / maxTarget) * 100) : 0;
      fill.style.width = `${percent}%`;
    }

    let nextMilestone = null;

    markerEls.forEach((marker) => {
      const target = Number(marker.dataset.milestoneTargetCents || 0);
      const unlocked = totalCents >= target;
      marker.classList.toggle('is-unlocked', unlocked);

      if (!unlocked && target > 0 && (!nextMilestone || target < nextMilestone.target)) {
        nextMilestone = {
          target,
          label: marker.dataset.milestoneLabel || ''
        };
      }
    });

    cardEls.forEach((card) => {
      const target = Number(card.dataset.milestoneTargetCents || 0);
      card.classList.toggle('is-unlocked', totalCents >= target);
    });

    if (status) {
      status.classList.remove('cart-milestones__status--complete');

      if (nextMilestone) {
        const remaining = Math.max(0, nextMilestone.target - totalCents);
        status.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> for <strong>${this.escapeHtml(nextMilestone.label)}</strong>.`;
      } else {
        status.textContent = this.milestoneProgress.dataset.completeText || '';
        status.classList.add('cart-milestones__status--complete');
      }
    }
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }

    const currencyCode = this.milestoneProgress ? this.milestoneProgress.dataset.currencyCode : 'USD';
    const symbol = this.milestoneProgress ? this.milestoneProgress.dataset.currencySymbol : '$';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode
      }).format(cents / 100);
    } catch (error) {
      return `${symbol}${(cents / 100).toFixed(2)}`;
    }
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  async handleUpsellAdd(evt) {
    evt.preventDefault();

    const button = evt.target.closest('.js-cart-drawer-upsell-add');
    if (!button || button.disabled) return;

    const variantId = Number(button.dataset.variantId || 0);
    const variantPrice = Number(button.dataset.variantPrice || 0);
    const quantity = Number(button.dataset.quantity || 1);
    if (!variantId) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding...';

    try {
      if (this.milestoneProgress && variantPrice > 0) {
        const currentTotal = Number(this.milestoneProgress.dataset.currentTotalCents || 0);
        this.updateMilestoneProgress(currentTotal + (variantPrice * quantity));
      }

      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      document.dispatchEvent(
        new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error.message);
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = originalLabel;
    }
  }
}

window.customElements.define('cart-drawer', CartDrawer);
